import {debounce} from 'throttle-debounce';
import Observer from 'common/scripts/observer';
import {Utils} from 'common/scripts/utils';

const observer = new Observer();

class Header {
    constructor() {
        this.element = null;
        this.targetElement = null;
        this.height = null;

        this.scrollPosition = 0;

        this.classes = {
            scroll     : 'is-scrolled',
            show       : 'is-showed',
            hide       : 'is-hidden',
            animate    : 'is-animate',
            fix        : 'is-fixed',
            menuOpen   : 'is-menu-open',
            themeChange: 'is-theme-change'
        };

        this._changeState = this._changeState.bind(this);
        this.transitionTime = 300;
    }

    /**
     * Инициализирует модуль
     * @param {Object} options - Параметры
     */
    init(options) {
        if (!options.element) {
            return;
        }

        this.element = options.element;
        this.targetElement = options.targetElement || options.element.querySelector('.j-header__target');
        this.baseElement = options.baseElement || options.element.querySelector('.j-header__base');
        this.height = this.element.offsetHeight;
        this.heightBase = this.baseElement.offsetHeight;

        this._subscribe();
        this._initState();
        this._bindEvents();
    }

    _subscribe() {
        observer.subscribe('openMenu', () => {
            this.element.classList.add(this.classes.menuOpen);
        });

        observer.subscribe('closeMenu', () => {
            this.element.classList.remove(this.classes.menuOpen);
        });

        observer.subscribe('changeHeaderTheme', (theme) => {
            this._changeHeaderTheme(theme);
        });
    }

    /**
     * Ставит изначальное состояние
     * @private
     */
    _initState() {
        if (this.element.dataset.static) {
            return;
        }

        this.scrollPosition = Utils.getScrollTop();

        if (!this._isScrollInTarget()) {
            this.element.classList.add(this.classes.fix, this.classes.hide);
        }
    }

    /**
     * Обрабатывает события
     * @private
     */
    _bindEvents() {
        window.addEventListener('scroll', debounce(6, this._changeState));

        const events = ['resize', 'orientationchange'];

        events.forEach((event) => {
            window.addEventListener(event, () => {
                this.height = this.element.offsetHeight;
                this.heightBase = this.baseElement.offsetHeight;
                this._changeState();
            });
        });
    }

    /**
     * Изменяет состояние хэдера
     * @private
     */
    _changeState() {
        if (this.element.dataset.static) {
            return;
        }

        this.scrollPosition = Utils.getScrollTop();

        const {animate, fix, hide, show} = this.classes;

        if (this._isScrollUp()) {
            observer.publish('changeScrollDirection', 'up');
            if (this._isScrollInTarget()) {
                this.element.classList.remove(show);
                this.element.classList.add(hide);
            } else {
                this.element.classList.add(show, fix, animate);
                this.element.classList.remove(hide);
            }

            if (this._isScrollInTarget(this.height * -1)) {
                this.element.classList.remove(fix, animate, hide, show);
            }
        }

        if (this._isScrollDown() && !this._isScrollInTarget() && window.pageYOffset > this.heightBase) {
            this.element.classList.add(hide, animate);
            this.element.classList.remove(show);
        }

        if (this._isScrollDown()) {
            observer.publish('changeScrollDirection', 'down');
        }

        this.prevScrollPosition = this.scrollPosition;
    }

    /**
     * Проверяет скролл вверх
     * @returns {Boolean} - true - скролл вверх
     * @private
     */
    _isScrollUp() {
        const stepScrollTop = -1;

        // если разница между текущим и предыдущим значением сколла меньше константы
        return this.scrollPosition - this.prevScrollPosition < stepScrollTop;
    }

    /**
     * Проверяет скролл вниз
     * @returns {Boolean} - true - скролл вниз
     * @private
     */
    _isScrollDown() {
        const stepScrollBottom = 1;

        // если скролл больше высоты хедера, и разница между текущим и предыдущим значением скролла больше константы
        return this.scrollPosition > this.height &&
            (this.scrollPosition - this.prevScrollPosition) > stepScrollBottom;
    }

    /**
     * Проверяет, находится ли текущий сколл в рамках элемента-таргета
     * @param {Number} offset - отступ, который нужно добавить в расчеты
     * @returns {Boolean} - если true, згачит скролл в элементе
     * @private
     */
    _isScrollInTarget(offset = 0) {
        if (this.scrollPosition < 3) {
            return true;
        }

        if (!this.targetElement) {
            return false;
        }

        let targetHeight = this.targetElement.offsetHeight - offset;

        if (targetHeight < 0) {
            targetHeight = 0;
        }

        // Если текущий скролл больше высоты таргет-элемента
        return targetHeight > this.scrollPosition && this.scrollPosition;
    }

    /**
     * Меняет темы хэдера
     * @param {string} theme - новая тема хэдера
     * @private
     */
    _changeHeaderTheme(theme) {
        if (this.element.dataset.static) {
            return;
        }

        const themeClasses = Array.from(this.element.classList).filter((word) => {
            return word.includes('_theme_');
        });

        themeClasses.forEach((classToRemove) => {
            this.element.classList.remove(classToRemove);
        });

        this.element.classList.add(this.classes.themeChange);
        if (theme) {
            this.element.classList.add(theme);
        }

        setTimeout(() => {
            this.element.classList.remove(this.classes.themeChange);
        }, this.transitionTime);
    }
}

export default Header;
